import { decodeJwt } from 'jose';

import { supabase } from '@/integrations/supabase/client';
import { SUPABASE_AUTH_STORAGE_KEY } from '@/integrations/supabase/authStorageKey';
import { publishVoluntarySignOutUnlessSessionEnded, withdrawVoluntarySignOut } from '@/lib/sessionEnded';
import { hasStoredSupabaseSession } from '@/lib/storedSupabaseSession';

/**
 * ENT-4349 exception to ENT-5492 D2: synchronously delete the persisted auth
 * library session WITHOUT taking the supabase-js auth lock. Only for a
 * fail-closed path whose premise is a stalled lock or a failed library
 * sign-out. Everywhere else, sign out through the library.
 */
export function removeStoredAuthSessionWithoutLock(targets: Map<string, string> = readStoredAuthSessions()): void {
  markSignedOutVoluntarily(targets);
  removeStoredAuthSessions(targets);
}

/** Snapshot only this client's session before a bounded, lock-free exit. */
export function readClientAuthSession(): Map<string, string> | null {
  const sessions = new Map<string, string>();
  if (!SUPABASE_AUTH_STORAGE_KEY) return sessions;
  try {
    const value = localStorage.getItem(SUPABASE_AUTH_STORAGE_KEY);
    if (value !== null) sessions.set(SUPABASE_AUTH_STORAGE_KEY, value);
  } catch (error) {
    console.error('Failed to read the client auth session:', error);
    return null;
  }
  return sessions;
}

/** An access token's `session_id` claim; null when it cannot be read. */
export function sessionIdOfAccessToken(accessToken: string | null | undefined): string | null {
  if (!accessToken) return null;
  try {
    const sessionId = decodeJwt(accessToken).session_id;
    return typeof sessionId === 'string' && sessionId ? sessionId : null;
  } catch {
    return null;
  }
}

function sessionIdOfStoredValue(value: string): string | null {
  try {
    const accessToken = (JSON.parse(value) as { access_token?: unknown }).access_token;
    return typeof accessToken === 'string' ? sessionIdOfAccessToken(accessToken) : null;
  } catch {
    return null;
  }
}

/**
 * ENT-5492: mark the session(s) about to be signed out as ended by the app, so
 * no tab reads the library's SIGNED_OUT as a dead session. Call before any
 * app-initiated sign-out that bypasses `signOutThroughAuthLibrary`.
 */
export function markSignedOutVoluntarily(targets: Map<string, string> = readStoredAuthSessions()): void {
  // THIS client's session only: another project's or an old local stack's
  // `sb-*-auth-token` is never the session being signed out (fallback review
  // on PR #3573). Unknown → an unmatched mark, so the sign-out reads as ended.
  const own = SUPABASE_AUTH_STORAGE_KEY ? targets.get(SUPABASE_AUTH_STORAGE_KEY) : undefined;
  publishVoluntarySignOutUnlessSessionEnded(own === undefined ? null : sessionIdOfStoredValue(own));
}

/**
 * Whether THIS client's session is stored — by the client's own storage key,
 * never any `sb-*-auth-token`. Unknown → not stored.
 */
export function isClientSessionStored(): boolean {
  if (!SUPABASE_AUTH_STORAGE_KEY) {
    return false;
  }
  try {
    return Boolean(localStorage.getItem(SUPABASE_AUTH_STORAGE_KEY));
  } catch {
    return false;
  }
}

/**
 * An app sign-out reported failure. auth-js removes the session on most
 * failures anyway (and broadcasts SIGNED_OUT), so the mark is withdrawn only
 * while the session is genuinely still stored (fallback review on PR #3573).
 */
export function withdrawVoluntarySignOutIfSessionLive(): void {
  if (isClientSessionStored()) {
    withdrawVoluntarySignOut();
  }
}

function isAuthSessionKey(key: string | null): key is string {
  return Boolean(key && key.startsWith('sb-') && key.endsWith('-auth-token'));
}

/** The persisted auth library sessions, by storage key. */
function readStoredAuthSessions(): Map<string, string> {
  const sessions = new Map<string, string>();
  try {
    for (let index = 0; index < localStorage.length; index += 1) {
      const key = localStorage.key(index);
      const value = isAuthSessionKey(key) ? localStorage.getItem(key) : null;
      if (isAuthSessionKey(key) && value !== null) sessions.set(key, value);
    }
  } catch (error) {
    console.error('Failed to read the stored auth session:', error);
  }
  return sessions;
}

/**
 * A stored session's stable identity: the access token's `session_id` claim,
 * which survives token rotation (another tab refreshing the same session).
 * Falls back to the user identity, then the raw value when neither can be read.
 */
function storedSessionIdentity(value: string): string {
  const sessionId = sessionIdOfStoredValue(value);
  if (sessionId) return `session:${sessionId}`;
  let userId: unknown;
  try {
    const session = JSON.parse(value) as { access_token?: unknown; user?: { id?: unknown } };
    userId = session.user?.id;
    if (typeof session.access_token === 'string') {
      try { userId = decodeJwt(session.access_token).sub ?? userId; } catch { /* retain the parsed user identity */ }
    }
  } catch { /* an opaque slot can only be compared by its raw value */ }
  return typeof userId === 'string' && userId ? `user:${userId}` : `raw:${value}`;
}

export function sessionIdOfClientAuthSnapshot(sessions: Map<string, string> | null): string | null {
  const value = SUPABASE_AUTH_STORAGE_KEY ? sessions?.get(SUPABASE_AUTH_STORAGE_KEY) : undefined;
  return value ? sessionIdOfStoredValue(value) : null;
}

function hasReplacementClientSession(endingSessionId: string | null): boolean {
  const currentSessionId = sessionIdOfClientAuthSnapshot(readClientAuthSession());
  // Unknown storage cannot establish authority to preserve protected app state.
  return currentSessionId !== null && currentSessionId !== endingSessionId;
}

function storedSessionTokens(value: string | undefined): { accessToken: string; userId: string | null } | null {
  if (!value) return null;
  try {
    const session = JSON.parse(value) as { access_token?: unknown; user?: { id?: unknown } };
    if (typeof session.access_token !== 'string') return null;
    let userId: unknown = session.user?.id;
    try { userId = decodeJwt(session.access_token).sub ?? userId; } catch { /* retain the parsed user identity */ }
    return { accessToken: session.access_token, userId: typeof userId === 'string' && userId ? userId : null };
  } catch {
    return null;
  }
}

const REPLACEMENT_VALIDITY_TIMEOUT_MS = 5000;

/**
 * Whether GoTrue still recognises this access token's session for this user.
 * A raw, lock-free request: the SDK's getUser removes the stored session on
 * `session_not_found` before the app can mark that removal as its own.
 */
async function isServerSessionLive(accessToken: string, userId: string): Promise<boolean> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), REPLACEMENT_VALIDITY_TIMEOUT_MS);
  try {
    const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/auth/v1/user`, {
      headers: {
        apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY ?? '',
        Authorization: `Bearer ${accessToken}`,
      },
      signal: controller.signal,
    });
    if (!response.ok) return false;
    const user = await response.json() as { id?: unknown } | null;
    return user?.id === userId;
  } catch {
    return false;
  } finally {
    clearTimeout(timer);
  }
}

/**
 * ENT-6102 D2: global sign-out revokes every session of its user on the server
 * (GoTrue `Logout(user.id)`), including a same-user replacement that signed in
 * while the request was in flight. Storage cannot undo that, so such a
 * replacement is kept only when GoTrue confirms it is still live; revoked or
 * unknown fails closed. A different user's replacement is never touched.
 * Returns whether it removed the replacement: the un-marked SIGNED_OUT then
 * hands the exit to the canonical once-guarded dead-session teardown
 * (ENT-5492: one session-ended notice, one landing on /auth).
 */
async function retireSameUserReplacementRevokedBy(endingUserId: string | null): Promise<boolean> {
  const replacement = readClientAuthSession();
  const tokens = storedSessionTokens(SUPABASE_AUTH_STORAGE_KEY ? replacement?.get(SUPABASE_AUTH_STORAGE_KEY) : undefined);
  if (!replacement || !tokens) return false;
  // Only a known different user is outside the revocation; an unknown identity is verified.
  if (tokens.userId && endingUserId && tokens.userId !== endingUserId) return false;
  if (tokens.userId && await isServerSessionLive(tokens.accessToken, tokens.userId)) return false;
  // Never marked voluntary: the server ended it, not its own user.
  removeStoredAuthSessions(replacement);
  return true;
}

export type AuthLibrarySignOutOutcome = 'owned' | 'superseded' | 'aborted';

/**
 * Remove exactly these sessions: a key that now holds a DIFFERENT session
 * (another tab signed in) is kept; the same session with rotated tokens is
 * still the one being signed out, so it is removed.
 */
function removeStoredAuthSessions(targets: Map<string, string>): void {
  const removedKeys: string[] = [];
  try {
    targets.forEach((value, key) => {
      const current = localStorage.getItem(key);
      if (current !== null && storedSessionIdentity(current) === storedSessionIdentity(value)) {
        localStorage.removeItem(key);
        removedKeys.push(key);
      }
    });
  } catch (error) {
    console.error('Failed to remove the stored auth session:', error);
  }
  removedKeys.forEach(broadcastSignedOut);
}

/**
 * Tell every tab, as the auth library itself would: supabase-js broadcasts
 * auth events on a BroadcastChannel named after its storage key, and each
 * tab's client re-emits a received event to its `onAuthStateChange`
 * subscribers. A lock-free removal skips the library, so it sends that
 * SIGNED_OUT itself.
 */
function broadcastSignedOut(storageKey: string): void {
  if (typeof globalThis.BroadcastChannel !== 'function') return;
  try {
    const channel = new globalThis.BroadcastChannel(storageKey);
    channel.postMessage({ event: 'SIGNED_OUT', session: null });
    channel.close();
  } catch (error) {
    console.error('Failed to broadcast the sign-out to other tabs:', error);
  }
}

/**
 * ENT-5492 D2: sign the session out THROUGH the auth library, so every tab
 * learns of it, and fail closed. supabase-js removes its stored session on
 * almost every outcome; when it reports an error or throws AND a session is
 * still stored, the stored session is removed lock-free and the SIGNED_OUT is
 * broadcast to every tab, so a reload can never restore a
 * session the app has just ended. Call `clearLocalAuthState()` (app
 * keys) afterwards.
 */
export async function signOutThroughAuthLibrary(
  scope: 'global' | 'local',
  operation?: { signal?: AbortSignal; targets?: Map<string, string>; expectedSessionId?: string | null },
): Promise<AuthLibrarySignOutOutcome> {
  // Default callers retain ownership until the SDK settles. A bounded caller
  // MUST retire the dying client's storage and dispose callbacks before leaving.
  // This signal fences our continuation only; it does not cancel SDK transport.
  if (operation?.signal?.aborted) return 'aborted';
  const targetSessions = operation?.targets ?? readStoredAuthSessions();
  const endingSessionId = sessionIdOfClientAuthSnapshot(targetSessions);
  const expectedSessionId = operation?.expectedSessionId !== undefined ? operation.expectedSessionId : endingSessionId;
  if (hasReplacementClientSession(expectedSessionId)) return 'superseded';
  // App-initiated: other tabs (and this one) must not read it as a dead session.
  markSignedOutVoluntarily(targetSessions);
  let libraryFailed = false;
  try {
    const result = await supabase.auth.signOut({ scope });
    if (operation?.signal?.aborted) return 'aborted';
    libraryFailed = !result || result.error !== null;
  } catch (error) {
    if (operation?.signal?.aborted) return 'aborted';
    libraryFailed = true;
    console.error(`Auth library ${scope} sign-out failed:`, error);
  }

  if (libraryFailed && hasStoredSupabaseSession()) {
    removeStoredAuthSessions(targetSessions);
  }
  if (scope === 'global' && hasReplacementClientSession(endingSessionId)) {
    const endingUserId = storedSessionTokens(
      SUPABASE_AUTH_STORAGE_KEY ? targetSessions.get(SUPABASE_AUTH_STORAGE_KEY) : undefined,
    )?.userId ?? null;
    const retired = await retireSameUserReplacementRevokedBy(endingUserId);
    if (operation?.signal?.aborted) return 'aborted';
    // The canonical dead-session teardown owns the exit, not this caller.
    if (retired) return 'superseded';
  }
  // Continuation ownership, not a guarantee that global server revocation succeeded.
  return hasReplacementClientSession(endingSessionId) ? 'superseded' : 'owned';
}
